import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useEditor, EditorContent } from '@tiptap/react'
import { buildExtensions } from './extensions'
import { Toolbar } from './Toolbar'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`

const Label = styled.label`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.muted};
`

const Helper = styled.div`
  font-size: 12px;
  color: ${(p) => (p.$error ? p.theme.colors.danger : p.theme.colors.muted)};
`

const sizeFont = {
  sm: '14px',
  md: '15px',
  lg: '16px'
}

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  ${(p) => p.$focused && css`
    border-color: ${p.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(41,98,255,0.2);
  `}
  ${(p) => p.$error && css`
    border-color: ${p.theme.colors.danger};
    box-shadow: 0 0 0 3px rgba(211,47,47,0.2);
  `}
  ${(p) => p.$disabled && css`
    opacity: 0.6;
    pointer-events: none;
  `}
  ${(p) => p.$readOnly && css`
    border-color: transparent;
    background: transparent;
  `}
`

const EditorArea = styled.div`
  font-size: ${(p) => sizeFont[p.$size] || sizeFont.md};
  line-height: 1.5;
  padding: ${(p) => (p.$readOnly ? '0' : '8px 12px')};
  min-height: ${(p) => p.$minHeight || '120px'};
  ${(p) => p.$readOnly && css`min-height: 0;`}

  .ProseMirror {
    outline: none;
    min-height: ${(p) => (p.$readOnly ? '0' : 'inherit')};
  }
  .ProseMirror p { margin: 0 0 8px; }
  .ProseMirror p:last-child { margin-bottom: 0; }
  .ProseMirror h1 { font-size: 1.6em; margin: 0 0 8px; line-height: 1.25; font-weight: 700; }
  .ProseMirror h2 { font-size: 1.35em; margin: 0 0 8px; line-height: 1.3; font-weight: 700; }
  .ProseMirror h3 { font-size: 1.15em; margin: 0 0 8px; line-height: 1.35; font-weight: 600; }
  .ProseMirror ul, .ProseMirror ol { margin: 0 0 8px; padding-left: 22px; }
  .ProseMirror li > p { margin: 0; }
  .ProseMirror blockquote {
    margin: 0 0 8px;
    padding: 4px 0 4px 12px;
    border-left: 3px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.muted};
  }
  .ProseMirror a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
  .ProseMirror code {
    background: ${({ theme }) => theme.colors.border};
    padding: 1px 4px;
    border-radius: 4px;
    font-size: 0.9em;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  }
  .ProseMirror p.is-editor-empty:first-child::before {
    content: attr(data-placeholder);
    float: left;
    color: ${({ theme }) => theme.colors.muted};
    pointer-events: none;
    height: 0;
  }
`

const isHtmlEmpty = (html) => !html || html === '<p></p>' || html === '<p><br></p>'

export const RichTextArea = React.forwardRef(function RichTextArea(
  {
    value = '',
    onChange,
    formatting = false,
    editable = true,
    placeholder,
    label,
    error,
    helperText,
    size = 'md',
    disabled = false,
    minHeight,
    features,
    extraExtensions,
    onBlur,
    onFocus,
    name,
    autofocus = false
  },
  ref
) {
  const [focused, setFocused] = useState(false)
  const lastEmittedHtml = useRef(value || '')

  const extensions = useMemo(
    () => buildExtensions({ placeholder, extraExtensions }),
    [placeholder, extraExtensions]
  )

  const editor = useEditor(
    {
      extensions,
      content: value || '',
      editable: editable && !disabled,
      autofocus,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        const next = isHtmlEmpty(html) ? '' : html
        if (next !== lastEmittedHtml.current) {
          lastEmittedHtml.current = next
          onChange?.(next)
        }
      },
      onFocus: () => {
        setFocused(true)
        onFocus?.()
      },
      onBlur: () => {
        setFocused(false)
        onBlur?.()
      }
    },
    [extensions, editable, disabled]
  )

  React.useImperativeHandle(ref, () => editor, [editor])

  useEffect(() => {
    if (!editor) return
    const incoming = value || ''
    const current = editor.getHTML()
    const currentNorm = isHtmlEmpty(current) ? '' : current
    if (incoming !== currentNorm && incoming !== lastEmittedHtml.current) {
      lastEmittedHtml.current = incoming
      editor.commands.setContent(incoming || '', false)
    }
  }, [value, editor])

  useEffect(() => {
    if (editor) editor.setEditable(editable && !disabled)
  }, [editor, editable, disabled])

  const showToolbar = formatting && editable && !disabled

  return (
    <Wrapper>
      {label && <Label htmlFor={name}>{label}</Label>}
      <Shell
        $focused={focused && editable}
        $error={!!error}
        $disabled={disabled}
        $readOnly={!editable}
      >
        {showToolbar && <Toolbar editor={editor} features={features} />}
        <EditorArea $size={size} $minHeight={minHeight} $readOnly={!editable}>
          <EditorContent editor={editor} />
        </EditorArea>
      </Shell>
      {(helperText || error) && <Helper $error={!!error}>{error || helperText}</Helper>}
    </Wrapper>
  )
})

export const RichTextView = React.forwardRef(function RichTextView({ value, ...rest }, ref) {
  return <RichTextArea ref={ref} value={value} editable={false} formatting={false} {...rest} />
})
